import { useSelect } from 'downshift'
import React from 'react'

import { DropdownItem } from 'components/common/Dropdown'
import Menu from 'components/common/Menu'
import Text from 'components/common/Text'
import Icon from 'components/Icon'
import theme from 'theme'

interface FilterSelectProps {
  label?: string
  items: any[]
  name?: string
  selectedValue: any
  onSelected: (...args: any[]) => any
  getFilterDisplayTitle?: (...args: any[]) => any
  hideLabel?: boolean
  textColor?: string
}

const FilterSelect: React.FC<FilterSelectProps> = ({
  getFilterDisplayTitle = (label) => label,
  label = '',
  items,
  selectedValue,
  onSelected,
  hideLabel,
  textColor,
  ...rest
}) => {
  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps
  } = useSelect({
    items,
    selectedItem: selectedValue,
    onSelectedItemChange: ({ selectedItem }) => {
      onSelected(selectedItem.value)
    }
  })

  const valuesByLabel = Object.fromEntries(
    (items || []).map((item) => [item.value, item.label])
  )

  return (
    <div
      css={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr)',
        gap: theme.space[2]
      }}
      {...rest}
    >
      {!hideLabel && (
        <label {...getLabelProps()}>
          <Text as="small">{getFilterDisplayTitle(label)}:</Text>
        </label>
      )}
      <div
        aria-roledescription="button"
        css={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumn: 'auto',
          justifyContent: 'start',
          alignItems: 'center',
          gap: theme.space[2],
          cursor: 'pointer'
        }}
        data-test-filter-select-trigger={label}
        {...getToggleButtonProps()}
      >
        <Text
          overflowEllipsis
          as="p"
          color="medium"
          css={{
            color: textColor,
            fontWeight: theme.fontWeights[1],
            textTransform: 'uppercase',
            fontSize: theme.fontSizes[0]
          }}
        >
          {valuesByLabel[selectedValue] || 'All'}
        </Text>
        <Icon
          css={{
            fontSize: theme.fontSizes[0],
            color: textColor || theme.colors.steel[1]
          }}
          icon="solidCaretDown"
        />
      </div>
      <Menu {...getMenuProps()} isOpen={isOpen}>
        {isOpen &&
          items.map((item, index) => (
            <DropdownItem
              key={item.value}
              getItemProps={getItemProps}
              highlightedIndex={highlightedIndex}
              index={index}
              item={item}
              selectedValue={selectedValue || 'All'}
            />
          ))}
      </Menu>
    </div>
  )
}

export default FilterSelect
